/**
 * MODULE: GlobalEmailTemplateEditHost
 * PURPOSE: Single app-level EmailTemplateEditModal for assistant form_catalog and template library.
 */

import { useCallback, useEffect, useState } from 'react';
import type { EmailTemplateEditOpenOptions } from '@shared/lib/frontend/utils/emailTemplateEditBridge';
import { subscribeOpenEmailTemplateEdit } from '@shared/lib/frontend/utils/emailTemplateEditBridge';
import {
  notifyEmailTemplateCreated,
  triggerEmailTemplateLearningRefresh,
} from '@shared/lib/frontend/utils/emailTemplateCreateBridge';
import { useToast } from '@design-system/components';
import EmailTemplateEditModal from './EmailTemplateEditModal';

export function GlobalEmailTemplateEditHost() {
  const [open, setOpen] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [successHint, setSuccessHint] = useState<EmailTemplateEditOpenOptions['successHint']>();
  const [coachInAssistant, setCoachInAssistant] = useState(false);
  const { showToast } = useToast();

  useEffect(
    () =>
      subscribeOpenEmailTemplateEdit((options) => {
        setTemplateId(options.templateId);
        setSuccessHint(options.successHint);
        setCoachInAssistant(Boolean(options.coachInAssistant));
        setOpen(true);
      }),
    []
  );

  const handleSuccess = useCallback(
    (result?: { title?: string }) => {
    setOpen(false);
    setTemplateId(null);
    triggerEmailTemplateLearningRefresh();
    notifyEmailTemplateCreated({
      successHint,
      mode: 'edit',
      templateTitle: result?.title,
      coachInAssistant,
    });
    setSuccessHint(undefined);
    setCoachInAssistant(false);
    showToast({
      message: 'Email template updated. Assistant will rank it by recent sends.',
      variant: 'success',
    });
  },
    [showToast, successHint, coachInAssistant]
  );

  return (
    <EmailTemplateEditModal
      isOpen={open}
      templateId={templateId}
      onClose={() => {
        setOpen(false);
        setTemplateId(null);
        setSuccessHint(undefined);
        setCoachInAssistant(false);
      }}
      onSuccess={handleSuccess}
    />
  );
}
